<template>
  <div class="my-8">
    <v-row align="top">
      <v-col cols="3">
        <h4 class="">{{ $t('nap.weather') }}</h4>
        <v-select
          density="compact"
          variant="outlined"
          hide-details
          :items="weatherOptions"
          item-title="label"
          item-value="value"
          v-model="weather"
        >
        </v-select>
      </v-col>
      <v-col cols="9">
        <div class="d-flex align-center">
          <h4 class="mr-4">{{ $t('nap.temperatureHumidity') }}</h4>
          <v-btn variant="outlined" color="primary" small @click="openModal">+ {{ $t('common.add') }}</v-btn>
        </div>
        <div class="my-2">
          <span
            v-for="(ac, i) in airConditions"
            :key="i"
            class="mr-2"
          > 
            <span v-if="i > 0" class="mr-2"> | </span>
            <span class="font-weight-bold mr-2">{{ ac.time }}:00 {{ ac.temperature }}℃ {{ ac.humidity }}%</span>
            <v-btn
              icon
              x-small
              :disabled="isDeleting"
              @click="deleteAirCondition(ac.id)"
              variant="text"
            ><v-icon x-small>mdi-close</v-icon></v-btn>
          </span>
        </div>
      </v-col>
      <v-progress-linear
        v-if="isDeleting"
        class="my-2"
        indeterminate
        color="primary"
      ></v-progress-linear>
    </v-row>

    <v-dialog
      v-model="isAirConditionModalOpen"
      max-width="540"
    >
      <v-card>
        <div class="pa-4">
          <div class="d-flex mb-8">
            <v-spacer></v-spacer>
            <h4>{{ $t('nap.addTemperatureHumidity') }}</h4>
            <v-spacer></v-spacer>
          </div>
          <div>
            <div class="font-weight-bold">{{ $t('nap.time') }}</div>
            <div class="mb-4">
              <v-text-field
                variant="outlined"
                density="compact"
                hide-details
                type="number"
                min="0"
                max="24"
                suffix=":00"
                v-model="newAirConditionTime"
              ></v-text-field>
            </div>

            <div class="font-weight-bold">{{ $t('nap.temperature') }}</div>
            <div class="mb-4">
              <v-text-field
                variant="outlined"
                density="compact"
                hide-details
                type="number"
                suffix="℃"
                min="-10"
                max="60"
                v-model="newAirConditionTemper"
              ></v-text-field>
            </div>

            <div class="font-weight-bold">{{ $t('nap.humidity') }}</div>
            <div class="mb-4">
              <v-text-field
                variant="outlined"
                density="compact"
                hide-details
                type="number"
                suffix="%"
                min="0"
                max="100"
                v-model="newAirConditionHumi"
              ></v-text-field>
            </div>
          </div>

          <v-progress-linear
            color="primary"
            indeterminate
            v-if="isCreating"
            class="mb-4"
          ></v-progress-linear>

          <div class="d-flex">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="flat"
              large
              :disabled="!airConditionCreateButtonValid || isCreating"
              @click="addAirCondition"
            >{{ $t('common.add') }}</v-btn>
            <v-spacer></v-spacer>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import { napDetailModule } from '@/store/dataModules/napDetailModule';
import { NurseryNapAirConditionRequest, NurseryNapDetailResponse } from 'chaild-api/lib';
import { NurseryNapAirConditionEntity } from 'chaild-api/src/model/mysql/entity/nurseryNapAirConditionEntity';
import dayjs from 'dayjs';
import { defineComponent } from 'vue';

interface DataType {
  isAirConditionModalOpen: boolean;
  newAirConditionTime: number | null;
  newAirConditionTemper: number | null;
  newAirConditionHumi: number | null;

  isCreating: boolean;
  isDeleting: boolean;
  weatherOptions: Array<{label: string; value: string}>;
}

export default defineComponent({
  
  data: (): DataType => {
    return {
      isAirConditionModalOpen: false,
      newAirConditionTime: null,
      newAirConditionTemper: null,
      newAirConditionHumi: null,

      isCreating: false,
      isDeleting: false,
      weatherOptions: [
        {
          label: 'Sunny',
          value: 'sunny',
        },
        {
          label: 'Cloudy',
          value: 'cloudy',
        },
        {
          label: 'Rainy',
          value: 'rainy',
        },
      ],
    };
  },

  computed: {
    weather: {
      get() {
        if (napDetailModule.nap) {
          return napDetailModule.nap.weather;
        }
        return null;
      },
      set(value: string) {
        if (napDetailModule.nap) {
          const newNap = {...napDetailModule.nap};
          newNap.weather = value;
          napDetailModule.setNap(newNap);
          napDetailModule.updateNap();
        }
      }
    },
    airConditions: {
      get() {
        if (napDetailModule.nap) {
          const airConditions = [...napDetailModule.nap.airConditions];
          return airConditions.sort((ac1, ac2) => ac1.time - ac2.time);
        }
        return [];
      },
    },
    airConditionCreateButtonValid() {
      return (
        (this as any).newAirConditionTime !== null
        && (this as any).newAirConditionTemper !== null
        && (this as any).newAirConditionHumi !== null
      );
    }
  },

  methods: {
    async addAirCondition() {
      if (
        (this as any).newAirConditionTime !== null
        && (this as any).newAirConditionTemper !== null
        && (this as any).newAirConditionHumi !== null
      ) {

        const time = (typeof (this as any).newAirConditionTime === 'string')
          ? parseInt((this as any).newAirConditionTime)
          : (this as any).newAirConditionTime;
        
        const temperature = (typeof (this as any).newAirConditionTemper === 'string')
          ? parseFloat((this as any).newAirConditionTemper)
          : (this as any).newAirConditionTemper;

        const humidity = (typeof (this as any).newAirConditionHumi === 'string')
          ? parseFloat((this as any).newAirConditionHumi)
          : (this as any).newAirConditionHumi;

        const airCondition: NurseryNapAirConditionRequest = {
          time,
          temperature,
          humidity,
        };

        const newNap = {...napDetailModule.nap} as NurseryNapDetailResponse;

        const airConditions: NurseryNapAirConditionRequest[] | undefined = newNap.airConditions
          ? [
            ...newNap.airConditions.map((ac) => ({
              time: ac.time,
              temperature: ac.temperature,
              humidity: ac.humidity,
            })),
            airCondition
          ]
          : [airCondition];

        (this as any).isCreating = true;
        await napDetailModule.updateNap(airConditions);
        (this as any).isCreating = false;

        (this as any).newAirConditionTime = null;
        (this as any).newAirConditionTemper = null;
        (this as any).newAirConditionHumi = null;
        (this as any).isAirConditionModalOpen = false;
      }
    },
    async deleteAirCondition(id: number) {
      if (napDetailModule.nap) {
        const airConditions = [...napDetailModule.nap.airConditions];
        const filtered = airConditions.filter((ac) => ac.id !== id);

        (this as any).isDeleting = true;
        await napDetailModule.updateNap(filtered);
        (this as any).isDeleting = false;
      }
    },
    openModal() {
      const hour = dayjs().hour();
      (this as any).newAirConditionTime = hour;
      (this as any).isAirConditionModalOpen = true;
    }
  }
} as any)
</script>